import React, { useState } from 'react';
import API_BASE_URL from '../../../components/apiconfig/api-config';
import { useNavigate } from 'react-router-dom';
import { FaArrowLeft, FaTrash } from 'react-icons/fa';

const IssuingAuthoritiesForm = () => {
  const [formData, setFormData] = useState({
    authority_name: '',
    designation: '',
    authority_Status: 'Active'
  });

  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState('');
  const navigate = useNavigate();

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) { // 5MB limit
        setMessage({ type: 'error', text: 'File size should be less than 5MB' });
        return;
      }
      
      if (!file.type.startsWith('image/')) {
        setMessage({ type: 'error', text: 'Please select an image file' });
        return;
      }

      setSelectedFile(file);
      setPreviewUrl(URL.createObjectURL(file));
      setMessage(null);
    }
  };

  const removeImage = () => {
    setSelectedFile(null);
    setPreviewUrl('');
    setMessage(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.authority_name.trim()) {
      setMessage({ type: 'error', text: 'Authority Name is required' });
      return;
    }

    if (!selectedFile) {
      setMessage({ type: 'error', text: 'Please select a signature image' });
      return;
    }

    setLoading(true);
    setMessage(null);
    
    try {
      const submitFormData = new FormData();
      submitFormData.append('authority_name', formData.authority_name);
      submitFormData.append('designation', formData.designation);
      submitFormData.append('authority_Status', formData.authority_Status);
      submitFormData.append('signature_image', selectedFile);

      const res = await fetch(`${API_BASE_URL}/api/masters/issuing-authorities-post`, {
        method: 'POST',
        credentials: 'include',
        body: submitFormData,
      });
      
      const data = await res.json();
      
      if (res.ok) {
        setMessage({ type: 'success', text: 'Issuing Authority Created Successfully!' });
        setFormData({
          authority_name: '',
          designation: '',
          authority_Status: 'Active'
        });
        setSelectedFile(null);
        setPreviewUrl('');
      } else {
        setMessage({ type: 'error', text: data.error || 'Something went wrong' });
      }
    } catch (err) {
      console.error('Submit error:', err);
      setMessage({ type: 'error', text: 'Server Error: ' + err.message });
    }
    setLoading(false);
  };

  return (
    <div className="max-w-8xl mx-auto p-0 bg-white">
      <div className="flex justify-between items-center border-b border-gray-100 bg-gray-50 px-4 py-1.5">
        <h2 className="text-lg font-bold mb-0 text-left">Issuing Authority Entry</h2>
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="flex items-center gap-1 text-[#10a5e9] border border-[#10a5e9] px-2 py-0.5 rounded text-xs font-medium hover:bg-[#eaf6fd]"
        >
          <FaArrowLeft />
          Back
        </button>
      </div>
      <form onSubmit={handleSubmit} className="space-y-0">
        <div className="px-4 pt-4 pb-2">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium mb-1">Authority Name</label>
              <input
                type="text"
                name="authority_name"
                value={formData.authority_name}
                onChange={handleChange}
                className="w-full border border-gray-300 px-3 py-1.5 text-sm bg-white focus:outline-none focus:border-[#10a5e9] rounded-none"
                required
              />
            </div>
            <div>
              <label className="block text-xs font-medium mb-1">Designation</label>
              <input
                type="text"
                name="designation"
                value={formData.designation}
                onChange={handleChange}
                className="w-full border border-gray-300 px-3 py-1.5 text-sm bg-white focus:outline-none focus:border-[#10a5e9] rounded-none"
                placeholder="e.g., Director, Manager, Officer"
              />
            </div>
            <div>
              <label className="block text-xs font-medium mb-1">Signature Image</label>
              <div className="space-y-2">
                <div className="flex gap-2">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleFileSelect}
                    className="flex-1 border border-gray-300 px-3 py-1.5 text-sm bg-white focus:outline-none focus:border-[#10a5e9] rounded-none"
                    required
                  />
                </div>
                
                {previewUrl && (
                  <div className="relative inline-block">
                    <img 
                      src={previewUrl} 
                      alt="Preview" 
                      className="max-w-32 max-h-32 border border-gray-300 rounded"
                    />
                    <button
                      type="button"
                      onClick={removeImage}
                      className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs hover:bg-red-600"
                    >
                      <FaTrash />
                    </button>
                  </div>
                )}
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium mb-1">Status</label>
              <select
                name="authority_Status"
                value={formData.authority_Status}
                onChange={handleChange}
                className="w-full border border-gray-300 px-3 py-1.5 text-sm bg-white focus:outline-none focus:border-[#10a5e9] rounded-none"
                required
              >
                <option value="Active">Active</option>
                <option value="Inactive">Inactive</option>
              </select>
            </div>
          </div>
        </div>
        <div className="px-4 pb-4">
          <button
            type="submit"
            className="bg-[#10a5e9] text-white px-6 py-2 rounded hover:bg-[#0e8dc6] font-semibold text-sm"
            disabled={loading}
          >
            {loading ? 'Saving...' : 'Save'}
          </button>
          {message && (
            <span className={`ml-4 text-sm ${message.type === 'success' ? 'text-green-600' : 'text-red-600'}`}>{message.text}</span>
          )}
        </div>
      </form>
    </div>
  );
};

export default IssuingAuthoritiesForm; 